import { useMemo } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { Reference } from 'types/reference';

export interface HeroProps {
  image: ImageProps;
  title: string;
  subtitle: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  ctaLabel2?: string;
  ctaReference2?: Reference;
  isPriority: boolean;
  imageQuality: number;
}

const Hero: React.FC<HeroProps> = ({
  image,
  title,
  subtitle,
  ctaLabel,
  ctaReference,
  ctaLabel2,
  ctaReference2,
  isPriority,
  imageQuality,
}) => {
  const cropFocus = useMemo(() => {
    if (image.gravity?.coordinates) {
      const right = Math.ceil((image.gravity?.coordinates.x / (image.media?.width as number)) * 100);
      const top = Math.ceil((image.gravity?.coordinates.y / (image.media?.height as number)) * 100);

      return `right ${right.toString()}% top ${top.toString()}%`;
    }

    if (image.gravity?.mode === 'center') {
      return 'center';
    }

    return 'initial';
  }, [image]);

  return (
    <div className="relative w-full rounded-3xl px-16 py-8 md:py-48">
      {image && (
        <div className="relative h-[748px] rounded-3xl md:h-[532px] lg:h-[668px]">
          <Image
            {...image}
            alt={title}
            fill
            style={{ objectFit: 'cover', objectPosition: cropFocus }}
            className="rounded-3xl"
            priority={isPriority}
            fetchPriority={isPriority ? 'high' : 'auto'}
            quality={imageQuality}
          />
        </div>
      )}
      <div className="absolute left-1/2 top-1/2 w-full max-w-[85%] -translate-x-1/2 -translate-y-1/2 text-center md:max-w-[60%]">
        <h1 className="text-center text-40 font-bold leading-[42px] text-white md:mt-32 md:text-72 md:leading-[68px]">
          {title}
        </h1>
        <p className="mt-24 text-center text-16 font-semibold text-white md:text-18">{subtitle}</p>

        {(ctaLabel || ctaLabel2) && (
          <div className="mt-22 flex justify-center gap-16 md:mt-36 lg:mt-32">
            {ctaLabel && (
              <Link link={ctaReference}>
                <Button className="rounded-xl bg-white md:px-48 md:py-12">
                  <span className="text-12 font-bold uppercase text-black">{ctaLabel}</span>
                </Button>
              </Link>
            )}
            {ctaLabel2 && (
              <Link link={ctaReference2}>
                <Button className="rounded-xl bg-white md:px-48 md:py-12">
                  <span className="text-12 font-bold uppercase text-black">{ctaLabel2}</span>
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Hero;
